import { useCallback, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import RulesSheet from './RulesSheet'
import { RulesContext, type RulesController } from './rulesContext'
import type { RulesSectionId } from './content'

/**
 * Holds the rules navigation state and renders the ⓘ sheet. Wraps the whole
 * route switch — see `rulesContext.ts` for why this is a context at all.
 */
export function RulesProvider({ children }: { children: ReactNode }) {
  const [sheetSection, setSheetSection] = useState<RulesSectionId | null>(null)
  const [screen, setScreen] = useState<{ section: RulesSectionId | null } | null>(null)

  const openSheet = useCallback((id: RulesSectionId) => setSheetSection(id), [])
  const closeSheet = useCallback(() => setSheetSection(null), [])
  // Opening the screen always drops the sheet: they explain the same thing, and leaving the panel
  // mounted behind a pushed page means backing out of the page lands on a stale sheet.
  const openScreen = useCallback((id?: RulesSectionId) => {
    setSheetSection(null)
    setScreen({ section: id ?? null })
  }, [])
  const closeScreen = useCallback(() => setScreen(null), [])

  const value = useMemo<RulesController>(
    () => ({
      sheetSection,
      openSheet,
      closeSheet,
      isScreenOpen: screen !== null,
      screenSection: screen?.section ?? null,
      openScreen,
      closeScreen,
    }),
    [sheetSection, screen, openSheet, closeSheet, openScreen, closeScreen]
  )

  return (
    <RulesContext.Provider value={value}>
      {children}
      {sheetSection && (
        <RulesSheet
          sectionId={sheetSection}
          onClose={closeSheet}
          onSeeAllRules={() => openScreen(sheetSection)}
        />
      )}
    </RulesContext.Provider>
  )
}
